import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChildNotificationInterface, ItemCommunicationService, ParentNotificationInterface, ParentTriggerInterface } from 'src/app/features/items/components/items-module/items-tab/services/item-communication.service';
import { AlternativeTranslate, Categories, Components, Items, ItemsImages } from 'tilby-models';
import { AllergenWithCheck, ItemDetailsSectionFormGroups } from './item-details-section.model';
import { ItemDetailsSectionFormService } from './item-details-section-form.service';
import { CustomForm, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { ItemsForm } from 'src/app/features/items/models/items-form.model';
import { ImagesManagerService } from 'src/app/core/services/images-manager';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { filter, map, Observable, of, startWith } from 'rxjs';
import { AlertDialogService } from 'src/app/dialogs';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { ItemDetailOptionInterface } from 'src/app/features/items/interfaces/item-details.interfaces';
import { itemIcons } from 'src/app/icons/icons';
import { ListSelectorDialogService } from 'src/app/dialogs/list-selector-dialog/list-selector-dialog.component';
import { ItemDataOperationsService, TranslationsInputData } from 'src/app/features/items/services/item-data-operations.service';
import { LeanPMSChainProduct } from 'src/app/features/digital-payments';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { ConfigurationManagerService } from 'src/app/core';

@Component({
  selector: 'app-item-details-section',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSelectModule, MatSlideToggleModule,
    MatChipsModule, MatIconModule, MatAutocompleteModule,
    MatCheckboxModule, MatRadioModule, MatButtonModule,
    MatTooltipModule, TrimInputDirective
  ],
  templateUrl: './item-details-section.component.html',
  styleUrl: './item-details-section.component.scss'
})
export class ItemDetailsSectionComponent implements OnInit, OnDestroy {

  readonly itemDetailsSectionFormService = inject(ItemDetailsSectionFormService);
  private readonly itemDataOperationsService = inject(ItemDataOperationsService);
  private readonly itemCommunicationService = inject(ItemCommunicationService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly imagesManagerService = inject(ImagesManagerService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly listSelectorDialogService = inject(ListSelectorDialogService);
  private readonly translate = inject(TranslateService);
  private readonly configurationManager = inject(ConfigurationManagerService);

  protected itemDetailsFormImages = new CustomFormGroup<CustomForm<Partial<{[prop in keyof ItemsForm]:string}>>>({});  
  protected isAlternativeTranslateEnabled = !!this.configurationManager.getPreference('items.enable_alternative_translate');

  // Form control for the autocomplete input
  componentCtrl = new FormControl('');
  // Filtered component suggestions for the autocomplete dropdown
  filteredComponents: Observable<Components[]> = of([]);
  // Input field for component selection in autocomplete
  @ViewChild('componentInput') componentInput!: ElementRef<HTMLInputElement>;
  
  @Input() item!: Items;

  fullComponentsList!: Array<Components> | undefined;
  componentsList: Array<Components> = [];
  allergensList!: Array<AllergenWithCheck> | undefined;
  optionNames: Array<ItemDetailOptionInterface> = [];
  chainProductsLeanPMS: Array<any> = [];
  isLeanPmsEnabled: boolean = false;
  category: Categories | undefined = undefined;
  
  iconExpand: boolean = false;
  icons:any = [];

  selectIcon(selectedLabel: string): void {
    let icon = this.itemDetailsSectionFormService.icon?.value;
    icon = icon === selectedLabel ? null : selectedLabel;
    this.itemDetailsSectionFormService.icon?.setValue(icon);
  }

  itemDetailsForm!: ItemDetailsSectionFormGroups;
  isDataListReady = this.itemDataCatalogService.isReady;
  itemDetailsFormReady = this.itemDetailsSectionFormService.itemDetailsSectionFormReady;

  constructor() {
    effect(() => {
      if(this.isDataListReady()) {
        this.readData();
        this.itemDetailsForm = this.itemDetailsSectionFormService.createForm(this.item);
      }
    }, { allowSignalWrites: true })

    effect(() => {
      // Listener per eseguire operazioni richieste dal parent
      const trigger = this.itemCommunicationService.parentTrigger();
      if(trigger?.child === 'details-tab' || trigger?.child === 'all') {
        this.handleParentTrigger(trigger);
      }
    })

    effect(() => {
      // Listener per leggere notifiche inviate dal parent
      const notification = this.itemCommunicationService.parentNotifier();
      if (notification?.child === 'details-tab') {
        this.handleParentNotification(notification);
      }
    })
  }

  handleParentTrigger(message: ParentTriggerInterface) {
    if(message.trigger === 'tabActivated') {
      this.requestCategory()
    }
    
    if(message.trigger === 'cancel') {
      this.itemDetailsSectionFormService.resetFormValues(this.item);
    }
  }

  requestCategory() {
    setTimeout(() => {
      this.itemCommunicationService.createParentTrigger('requestGeneralData', 'general', 'details-tab', { formField: 'category' })
    })
  }
  
  handleParentNotification(message: ParentNotificationInterface) {
    if(message.notification === 'generalData') {
      this.category = message.params?.category;
      this.filterInitialComponents();
      this.listenToComponentsSearch();
    }
  }
  
  ngOnInit(): void {
  }

  readData() {
    this.fullComponentsList = this.itemDataCatalogService.getDataByKey("components") || [];
    this.allergensList = this.itemDataCatalogService.getDataByKey("allergens") || [];
    this.icons = Object.entries(itemIcons).map(([label]) => ({ label, selected: false }));
    this.optionNames = this.itemDetailsSectionFormService.optionNames;
    
    this.itemDataOperationsService.isLeanPmsEnabled().then((result) => {
      this.isLeanPmsEnabled = result;
      if(this.isLeanPmsEnabled) {
        this.readChainProduction();
      }
    })
    
  }

  readChainProduction() {
    this.itemDataOperationsService.fetchChainProducts().then((result) => {
      if(result) {
        this.chainProductsLeanPMS = result;
      }
    })
  }

  selectChainProductsLeanPMS(option: ItemDetailOptionInterface) {
    this.listSelectorDialogService.openDialog({
        data: {
          optionList: this.chainProductsLeanPMS, 
          optionValue: this.chainProductsLeanPMS.find(prod => prod.id == this.itemDetailsSectionFormService.getOptionName(option.targetField)?.value)
        }
      }
    ).then((res: LeanPMSChainProduct | undefined) => {
      if(res) {
        this.itemDetailsSectionFormService.getOptionName(option.targetField)?.setValue(res.id)
      }

    })
  }

  filterInitialComponents() {
    if(this.fullComponentsList?.length) {
      this.componentsList = this.fullComponentsList.filter((c: Components) => 
          !c.categories || c.categories.length == 0 ||
          c.categories.some(cat => cat.id === this.category)
      )
    }
  }

  displayComponent(component: Components): string {
    return component?.name? component.name: '';
  }

  /**
   * Adds a component as a chip when selected from autocomplete preventing duplicates.
   */
  onSelectComponent(event: MatAutocompleteSelectedEvent): void {
    const selectedComponent = event.option.value as Components;

    // Check for duplicates
    const isDuplicate = this.itemDetailsSectionFormService.components?.value.some((c: Components) => c.id === selectedComponent.id);

    if (isDuplicate) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.DETAILS.COMPONENT_ALREADY_PRESENT')
      }});
    } else {
      const componentsList = this.itemDetailsSectionFormService.components?.value;
      componentsList.push(selectedComponent);
      this.itemDetailsSectionFormService.components?.setValue(componentsList);
    }

    // Clear the input and reset autocomplete
    this.componentInput.nativeElement.value = '';
    this.componentCtrl.setValue(null);
  }

  /**
   * Removes a component chip.
   */
  removeComponent(component: Components): void {
    const index = this.itemDetailsSectionFormService.components?.value.findIndex((c: Components) => c.id === component.id);
    if (index >= 0) {
      const componentsList = this.itemDetailsSectionFormService.components?.value
      componentsList.splice(index, 1);
      this.itemDetailsSectionFormService.components?.setValue(componentsList);
    }
  }

  
  listenToComponentsSearch() {
    this.filteredComponents = this.componentCtrl.valueChanges.pipe(
      startWith(null), 
      filter(value => typeof value === 'string' || value === null),
      map(value => (value ? this._filterComponents(value) : this.componentsList?.slice() || []))
    );
  }

  /**
   * Filters the available components based on the search query and item's category.
   */
  private _filterComponents(query: string): Components[] {
    const filterValue = query.toLowerCase();
    
    if(this.componentsList) {
      return this.componentsList.filter(component => 
        component.name.toLowerCase().startsWith(filterValue)
      );
    } else {
      return [];
    }
  }

  checkboxAllergenSelectionChange($event: any, allergen: any) {
    allergen.checked = $event.checked;
    this.itemDetailsForm.updateValueAndValidity();
  }

  sendNotificationToParent(notification: ChildNotificationInterface) {
    Promise.resolve().then(() => {
      this.itemCommunicationService.notifyParent(notification);
    });
  }

  manageAlternativeTranslate() {
    const config: TranslationsInputData = {
      mainValue: this.itemDetailsSectionFormService.description?.value,
      currentValues: this.itemDetailsSectionFormService.alternativeDescriptions?.value || [],
      maxLength: this.itemDetailsSectionFormService.validators.descriptionMaxLength
    }
    this.itemDataOperationsService.getUpdatedTranslations(config).then((updatedValues: AlternativeTranslate[] | null) => {
      if(updatedValues) this.itemDetailsSectionFormService.alternativeDescriptions?.setValue(updatedValues);
    })
  }


  // INSERIMENTO IMMAGINE 

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  maxImageWidth: number = 1280;
  maxImageHeight: number = 720;

  addNewImage() {
    this.fileInput.nativeElement.click();
  }

  handleFileInput(event: Event) {
    const input = event.target as HTMLInputElement;

    if(input.files?.length) {
      const newFile = input.files[0];
      this.processNewFile(newFile);
      input.value = ''
    }
  }

  private processNewFile(file: File) {
    ImagesManagerService.localLoadNewImage(file, this.maxImageWidth, this.maxImageHeight).then((result: any) => {
      const imagesList = this.itemDetailsSectionFormService.images?.value;
      imagesList.push(result);
      
      this.itemDetailsSectionFormService.images?.setValue(imagesList);
      if(!this.itemDetailsSectionFormService.thumbnail?.value && result.image_url) {
        this.itemDetailsSectionFormService.thumbnail?.setValue(result.image_url)
      }
    })
    .catch((reason) => {
      // invalid file
      switch (reason) {
        case 'not image':
          this.alertDialogService.openDialog({
            data: {
              messageLabel: this.translate.instant('ITEMS.DETAILS.COMPONENT_INVALID_IMAGE')
          }});
          break;
        default:
          this.alertDialogService.openDialog({
            data: {
              messageLabel: this.translate.instant('ITEMS.DETAILS.COMPONENT_PROBLEM_OPENING_IMAGE')
          }});
      }
    });
  }

  saveAllImages() {
    return this.imagesManagerService.saveAllImages(this.itemDetailsSectionFormService.images?.value);
  }

  removeImage(image: ItemsImages) {
    const imagesList = this.itemDetailsSectionFormService.images?.value;

    const index = imagesList.findIndex((c: ItemsImages) => c.image_url === image.image_url);
    if (index >= 0) {
      imagesList.splice(index, 1);
      this.itemDetailsSectionFormService.images?.setValue(imagesList);
    }
    ImagesManagerService.markToDelete(image);
  }

  // ---- INSERIMENTO IMMAGINE 


  ngOnDestroy(): void {
    this.itemDetailsSectionFormService.resetOptionNames();
  }
}
